import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  ListFilter,
  Plus,
  Search,
} from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionsContext';
import actionItemService from '../../services/actionItemService';
import ActionItemCreateModal from './ActionItemCreateModal';
import ActionItemStatusBadge, {
  getActionItemStatusTone,
} from './ActionItemStatusBadge';
import './ActionItems.css';

const ActionItemsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions();
  const canCreateActionItem = canCreate('action_items');
  const createOpen = (
    canCreateActionItem && searchParams.get('create') === '1'
  );
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    accountable_user: '',
    centre: '',
    ordering: '-created_at',
    pending_my_approval: '',
    page: 1,
  });
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [peopleOptions, setPeopleOptions] = useState({
    responsibles: [],
  });
  const [canFilterMyApprovals, setCanFilterMyApprovals] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;

    const fetchItems = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await actionItemService.list(filters);
        if (!active) return;
        const results = Array.isArray(response)
          ? response
          : response?.results || [];
        setItems(results);
        setCount(Array.isArray(response) ? response.length : response.count || 0);
        setNext(Array.isArray(response) ? null : response.next);
        setPrevious(Array.isArray(response) ? null : response.previous);
      } catch (requestError) {
        if (!active) return;
        setItems([]);
        setCount(0);
        setNext(null);
        setPrevious(null);
        setError(t('actionItems.errors.load'));
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchItems();
    return () => {
      active = false;
    };
  }, [filters, refreshKey, t]);

  useEffect(() => {
    let active = true;

    const fetchOptions = async () => {
      setOptionsError('');
      try {
        const response = await actionItemService.filterOptions();
        if (!active) return;
        setPeopleOptions({
          responsibles: response?.responsibles || [],
        });
        setCanFilterMyApprovals(
          response?.can_filter_my_approvals === true,
        );
      } catch (requestError) {
        if (active) setOptionsError(t('actionItems.errors.load'));
      }
    };

    fetchOptions();
    return () => {
      active = false;
    };
  }, [refreshKey, t]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
      page: field === 'page' ? value : 1,
    }));
  };

  const centreOptions = useMemo(
    () => Array.from(
      new Map(
        items
          .filter((item) => item.centre_details)
          .map((item) => [
            String(item.centre_details.id || item.centre),
            item.centre_details,
          ]),
      ).values(),
    ),
    [items],
  );
  const openItem = (id) => navigate(`/action-items/${id}`);
  const openCreate = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('create', '1');
    setSearchParams(nextParams);
  };
  const closeCreate = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    setSearchParams(nextParams, { replace: true });
  };
  const handleCreated = () => {
    closeCreate();
    setRefreshKey((current) => current + 1);
  };

  return (
    <div className="action-items-page" role="main">
      <header className="action-items-page-header">
        <div>
          <h1>{t('actionItems.title')}</h1>
          <p>{t('actionItems.subtitle')}</p>
        </div>
        {canCreateActionItem && (
          <button
            className="action-items-primary-button"
            type="button"
            onClick={openCreate}
          >
            <Plus size={18} aria-hidden="true" />
            {t('actionItems.new')}
          </button>
        )}
      </header>

      <button
        className="action-items-filters-toggle"
        type="button"
        aria-expanded={filtersOpen}
        aria-controls="action-items-filters"
        onClick={() => setFiltersOpen((current) => !current)}
      >
        <span>
          <ListFilter size={18} aria-hidden="true" />
          {t(
            filtersOpen
              ? 'actionItems.filters.hide'
              : 'actionItems.filters.show',
          )}
        </span>
        {filtersOpen
          ? <ChevronUp size={18} aria-hidden="true" />
          : <ChevronDown size={18} aria-hidden="true" />}
      </button>

      <section
        id="action-items-filters"
        className={[
          'action-items-filters',
          filtersOpen ? 'action-items-filters--open' : '',
        ].filter(Boolean).join(' ')}
        aria-label={t('actionItems.filters.title')}
      >
        <div
          className={[
            'action-items-filters-row',
            'action-items-filters-row--primary',
            canFilterMyApprovals
              ? 'action-items-filters-row--with-approval'
              : '',
          ].filter(Boolean).join(' ')}
        >
          <label className="action-items-search">
            <span>{t('actionItems.filters.search')}</span>
            <span className="action-items-input-icon">
              <Search size={17} aria-hidden="true" />
              <input
                value={filters.search}
                onChange={(event) => updateFilter('search', event.target.value)}
                placeholder={t('actionItems.filters.searchPlaceholder')}
              />
            </span>
          </label>

          <label>
            <span>{t('actionItems.filters.status')}</span>
            <select
              value={filters.status}
              onChange={(event) => updateFilter('status', event.target.value)}
            >
              <option value="">{t('actionItems.filters.allStatuses')}</option>
              {[
                'PENDING_APPROVAL',
                'PARTIALLY_APPROVED',
                'APPROVED',
                'OPEN',
                'IN_PROGRESS',
                'CLOSED',
                'DISAPPROVED',
                'CANCELLED',
              ].map((status) => (
                <option key={status} value={status}>
                  {t(`actionItems.status.${status}`)}
                </option>
              ))}
            </select>
          </label>

          {canFilterMyApprovals && (
            <label>
              <span>{t('actionItems.filters.approval')}</span>
              <select
                value={filters.pending_my_approval}
                onChange={(event) => (
                  updateFilter('pending_my_approval', event.target.value)
                )}
              >
                <option value="">
                  {t('actionItems.filters.allApprovals')}
                </option>
                <option value="true">
                  {t('actionItems.filters.pendingMine')}
                </option>
              </select>
            </label>
          )}
        </div>

        <div className="action-items-filters-row action-items-filters-row--secondary">
          <label>
            <span>{t('actionItems.filters.responsible')}</span>
            <select
              value={filters.accountable_user}
              onChange={(event) => (
                updateFilter('accountable_user', event.target.value)
              )}
            >
              <option value="">
                {t('actionItems.filters.allResponsibles')}
              </option>
              {peopleOptions.responsibles.map((responsible) => (
                <option key={responsible.id} value={responsible.id}>
                  {responsible.first_name} {responsible.last_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>{t('actionItems.filters.centre')}</span>
            <select
              value={filters.centre}
              onChange={(event) => updateFilter('centre', event.target.value)}
            >
              <option value="">{t('actionItems.filters.allCentres')}</option>
              {centreOptions.map((centre) => (
                <option key={centre.id} value={centre.id}>
                  {centre.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>{t('actionItems.filters.ordering')}</span>
            <select
              value={filters.ordering}
              onChange={(event) => updateFilter('ordering', event.target.value)}
            >
              <option value="-created_at">
                {t('actionItems.filters.newest')}
              </option>
              <option value="created_at">
                {t('actionItems.filters.oldest')}
              </option>
              <option value="due_date">
                {t('actionItems.filters.dueDate')}
              </option>
              <option value="training_course">
                {t('actionItems.filters.course')}
              </option>
              <option value="status">
                {t('actionItems.filters.status')}
              </option>
            </select>
          </label>
        </div>
      </section>

      <section className="action-items-panel">
        {loading && (
          <div className="action-items-state">
            {t('actionItems.loading')}
          </div>
        )}
        {!loading && (error || optionsError) && (
          <div className="action-items-state action-items-state--error">
            {error || optionsError}
          </div>
        )}
        {!loading && !error && !optionsError && items.length === 0 && (
          <div className="action-items-state">
            {t('actionItems.empty')}
          </div>
        )}
        {!loading && !error && !optionsError && items.length > 0 && (
          <>
            <div className="action-items-table-wrap">
              <table className="action-items-table">
                <colgroup>
                  <col className="action-items-col-course" />
                  <col className="action-items-col-responsible" />
                  <col className="action-items-col-problem" />
                  <col className="action-items-col-centre" />
                  <col className="action-items-col-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t('actionItems.trainingCourse')}</th>
                    <th>{t('actionItems.filters.responsible')}</th>
                    <th>{t('actionItems.gapProblemDetected')}</th>
                    <th>{t('actionItems.filters.centre')}</th>
                    <th>{t('actionItems.filters.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className={[
                        'action-item-row',
                        `action-item-row--${getActionItemStatusTone(
                          item.status,
                        )}`,
                      ].join(' ')}
                      tabIndex={0}
                      onClick={() => openItem(item.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openItem(item.id);
                        }
                      }}
                    >
                      <td
                        className="action-item-row-course"
                        data-label={t('actionItems.trainingCourse')}
                      >
                        <strong>{item.training_course}</strong>
                      </td>
                      <td data-label={t('actionItems.filters.responsible')}>
                        {item.accountable_user_details
                          ? [
                            item.accountable_user_details.first_name,
                            item.accountable_user_details.last_name,
                          ].filter(Boolean).join(' ')
                          : item.accountable || '—'}
                      </td>
                      <td
                        className="action-item-row-problem"
                        data-label={t('actionItems.gapProblemDetected')}
                      >
                        {item.gap_problem_detected || '—'}
                      </td>
                      <td data-label={t('actionItems.filters.centre')}>
                        {item.centre_details?.name || '—'}
                      </td>
                      <td data-label={t('actionItems.filters.status')}>
                        <ActionItemStatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <footer className="action-items-pagination">
              <span>{t('actionItems.total', { count })}</span>
              <div>
                <button
                  type="button"
                  disabled={!previous || filters.page <= 1}
                  onClick={() => updateFilter('page', filters.page - 1)}
                >
                  {t('common.previous')}
                </button>
                <span>{filters.page}</span>
                <button
                  type="button"
                  disabled={!next}
                  onClick={() => updateFilter('page', filters.page + 1)}
                >
                  {t('common.next')}
                </button>
              </div>
            </footer>
          </>
        )}
      </section>

      <ActionItemCreateModal
        open={createOpen}
        onClose={closeCreate}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default ActionItemsPage;
